// frontend-react/src/components/Layout/AdminLayout.js - NUEVO ARCHIVO
import React, { useState, useContext } from "react";
import {
  Box,
  Toolbar,
  IconButton,
  Typography,
  AppBar,
  Breadcrumbs,
  Link,
} from "@mui/material";
import { Menu as MenuIcon, NavigateNext, Brightness4, Brightness7, Home } from "@mui/icons-material";
import { useLocation, Link as RouterLink } from "react-router-dom";
import AdminSidebar from "./AdminSidebar";
import { useAuth } from "../../hooks/useAuth";
import { ColorModeContext } from "../../contexts/ThemeContext";

import { useTheme, useMediaQuery } from "@mui/material";

const AdminLayout = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const location = useLocation();
  const { user } = useAuth();
  const colorMode = useContext(ColorModeContext);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Close sidebar on route change on mobile
  React.useEffect(() => {
    if (isMobile && sidebarOpen) {
      setSidebarOpen(false);
    }
  }, [location.pathname, isMobile, sidebarOpen]);

  // Generar breadcrumbs desde la ruta actual
  const generateBreadcrumbs = () => {
    const paths = location.pathname.split("/").filter((path) => path);
    const breadcrumbs = [];

    // Agregar inicio
    breadcrumbs.push(
      <Link
        key="home"
        component={RouterLink}
        to="/admin"
        color="inherit"
        underline="hover"
      >
        Dashboard
      </Link>
    );

    // Agregar cada segmento de la ruta
    let currentPath = "";
    paths.forEach((path, index) => {
      if (path !== "admin") {
        currentPath += `/${path}`;
        const isLast = index === paths.length - 1;

        breadcrumbs.push(
          isLast ? (
            <Typography key={path} color="text.primary">
              {path.charAt(0).toUpperCase() + path.slice(1)}
            </Typography>
          ) : (
            <Link
              key={path}
              component={RouterLink}
              to={currentPath}
              color="inherit"
              underline="hover"
            >
              {path.charAt(0).toUpperCase() + path.slice(1)}
            </Link>
          )
        );
      }
    });

    return breadcrumbs;
  };

  return (
    <Box sx={{ display: "flex" }}>
      {/* AppBar */}
      <AppBar
        position="fixed"
        color="default"
        elevation={0}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backdropFilter: 'blur(20px)',
          backgroundColor: (theme) => 
            theme.palette.mode === 'dark' 
              ? 'rgba(30, 41, 59, 0.8)' 
              : 'rgba(255, 255, 255, 0.8)',
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          transition: (theme) => theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={toggleSidebar}
            edge="start"
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          <Box sx={{ flexGrow: 1 }}>
            <Breadcrumbs separator={<NavigateNext fontSize="small" />}>
              {generateBreadcrumbs()}
            </Breadcrumbs>
          </Box>

          {/* Botón Ir al Home */}
          <IconButton onClick={() => window.location.href = '/'} color="inherit" sx={{ mr: 1 }} title="Ir al Home">
            <Home />
          </IconButton>

          {/* Dark mode toggle */}
          <IconButton onClick={colorMode.toggleColorMode} color="inherit" sx={{ mr: 2 }}>
            {colorMode.mode === 'dark' ? <Brightness7 /> : <Brightness4 />}
          </IconButton>

          <Typography variant="body2" sx={{ mr: 2 }}>
            {user?.firstName || user?.email} ({user?.profile?.role})
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Sidebar - Solo visible en mobile */}
      {isMobile && (
        <AdminSidebar 
          open={sidebarOpen} 
          onClose={() => setSidebarOpen(false)} 
          variant="temporary"
        />
      )}

      {/* Contenido principal */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: '100%',
          transition: "width 0.3s ease, margin 0.3s ease",
        }}
      >
        <Toolbar /> {/* Espacio para el AppBar */}
        {children}
      </Box>
    </Box>
  );
};

export default AdminLayout;
